import b from 'b_';
import isEqual from 'lodash/isEqual';
import { DeferredRenderer, Text, useStaticUrls } from 'platform-components';
import { isRobotProblem } from 'platform-components/src/components/robot/utils/isRobotProblem';
import { getExactPathMatch } from 'platform-components/utils';
import qs from 'query-string';
import React from 'react';
import { useLocation, useParams } from 'react-router';
import ym from 'react-yandex-metrika';
import { accessibleClick } from '@edu-frontend/modules/src/shared/lib/a11y';

import { BLOCK_CARDS_COUNT } from 'common.components/constants';
import { ProblemWrapper } from 'common.components/problem-wrapper/problem-wrapper';
import { TEAM_BUILDER_ENTRY_STEPS } from 'common.components/team-builder/typings';

import BottomElementsAfterAddition from '../__bottom-elements-after-addition/theme-page__bottom-elements-after-addition';
import BottomElements from '../__bottom-elements/theme-page__bottom-elements';
import Placeholder from '../__placeholder/theme-page__placeholder';
import { ThemePageStackProblem } from '../__stack-problem/theme-page__stack-problem';

import { Props } from './theme-page__stack.container';

import './theme-page__stack.scss';

const ThemePageStack = (props: Props) => {
    const { search, pathname } = useLocation();
    const query = qs.parse(search);
    const staticUrls = useStaticUrls();
    const params = useParams<Record<string, string>>();

    const handleClick = () => {
        const {
            match: {
                params: { subject, themeId },
            },
            selectStack,
            stack,
        } = props;

        selectStack(stack.id);

        ym('reachGoal', 'teacher_lesson_creation_stack_clicked', {
            problemId: stack.representative.id,
            stackId: stack.id,
            subject,
            themeId,
        });
    };

    const handleAddToLesson = async (e: React.MouseEvent<HTMLElement>) => {
        const {
            addProblemToLesson,
            intl,
            toggleLessonWidget,
            toggleWidgetTooltip,
            problemsInLessonCount,
            setWizardVariables,
            stack,
            saveOrCreateLesson,
            hasNoTeams,
            startTeamBuilder,
        } = props;

        e.stopPropagation();

        if (hasNoTeams) {
            const { path = '' } = getExactPathMatch(staticUrls, pathname) || {};
            ym('reachGoal', 'click_add_class', { from: path, subject: params?.subject, grade: query?.grade });
            startTeamBuilder({
                entryPoint: TEAM_BUILDER_ENTRY_STEPS.TEAM,
            });
            return;
        }

        if (problemsInLessonCount >= BLOCK_CARDS_COUNT) {
            toggleWidgetTooltip(true);
            return;
        }

        addProblemToLesson(stack.representative, stack.id);
        saveOrCreateLesson({
            intl,
            onSuccess: (clessonId: number) => sendAddProblemGoal(clessonId),
        });
        toggleLessonWidget(true);

        setWizardVariables({ addProblemButtonClicked: true });
    };

    const handleRemoveFromLesson = (e: React.MouseEvent<HTMLElement>) => {
        const { removeAllStackProblemsFromLesson, deleteLesson, stack } = props;

        e.stopPropagation();

        removeAllStackProblemsFromLesson({
            problemId: stack.representative.id,
            stackId: stack.id,
            onSuccess: (cLessonId: number, isLastProblem: boolean) => {
                if (isLastProblem) {
                    deleteLesson({ cLessonId, isDraft: true });
                }
            },
        });
    };

    const sendAddProblemGoal = (clessonId: number) => {
        const {
            match: {
                params: { subject, themeId },
            },
            stack,
        } = props;

        ym('reachGoal', 'teacher_lesson_creation_intopic_add_item', {
            clessonId,
            problemId: stack.representative.id,
            stackId: stack.id,
            subject,
            themeId,
        });
    };

    const handleStackClick = () => {
        const { setWizardVariables } = props;

        setWizardVariables({ stackClicked: true });

        handleClick();
    };

    const {
        stack: { problems_count: problemsCount, representative, name, problems_used_in_course: problemsUsedInCourse },
        isLoading,
        currentStackProblemsCountInCurrentLesson,
        problemBlockPlateStyles,
        onProblemRef,
        shouldShowTag,
        canEditLesson,
    } = props;

    if (isLoading) {
        return <Placeholder problemsCount={problemsCount} shouldShowTag={shouldShowTag} />;
    }

    if (!problemsCount) {
        return null;
    }

    return (
        <div className="theme-page__item" {...accessibleClick(handleStackClick)} ref={onProblemRef}>
            <div className="theme-page__item-wrapper">
                <ProblemWrapper
                    mix={b('theme-page', 'problem', { robot: isRobotProblem(representative) })}
                    problem={representative}
                >
                    <DeferredRenderer>
                        <ThemePageStackProblem
                            problem={representative}
                            problemBlockPlateStyles={problemBlockPlateStyles}
                        />
                    </DeferredRenderer>
                </ProblemWrapper>
                {currentStackProblemsCountInCurrentLesson > 0 && (
                    <div className="theme-page__problem-blur">
                        <div className="theme-page__add-badge">
                            <Text
                                id="library.item.addVariantToLessonStatusSuccess"
                                size={Text.SIZE.M}
                                weight={Text.WEIGHT.M}
                            />
                        </div>
                    </div>
                )}
                <div className="theme-page__item-body">
                    {shouldShowTag && (
                        <div className="theme-page__item-tag">
                            <Text size={Text.SIZE.S} />
                        </div>
                    )}
                    <div className="theme-page__item-name">
                        <Text type={Text.TYPE.MARKDOWN}>{name}</Text>
                    </div>
                    {currentStackProblemsCountInCurrentLesson > 0 ? (
                        <BottomElementsAfterAddition
                            currentStackProblemsCountInCurrentLesson={currentStackProblemsCountInCurrentLesson}
                            onRemoveFromLesson={handleRemoveFromLesson}
                            problemsCount={problemsCount}
                        />
                    ) : (
                        <>
                            <div className="theme-page__item-description">
                                {problemsUsedInCourse > 0 && (
                                    <span className="theme-page__item-used">
                                        <Text
                                            id="library.item.usedLessonsAmount"
                                            size={Text.SIZE.S}
                                            values={{ count: problemsUsedInCourse }}
                                            weight={Text.WEIGHT.M}
                                        />
                                    </span>
                                )}
                            </div>
                            <BottomElements
                                canEditLesson={canEditLesson}
                                handleAddToLesson={handleAddToLesson}
                                problemsCount={problemsCount}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// eslint-disable-next-line complexity
export const ThemePageStackMemo = React.memo(ThemePageStack, (prevProps, nextProps) => {
    return (
        isEqual(prevProps.stack, nextProps.stack) &&
        prevProps.isLoading === nextProps.isLoading &&
        prevProps.currentStackProblemsCountInCurrentLesson === nextProps.currentStackProblemsCountInCurrentLesson &&
        isEqual(prevProps.problemBlockPlateStyles, nextProps.problemBlockPlateStyles) &&
        prevProps.onProblemRef.toString() === nextProps.onProblemRef.toString() &&
        prevProps.shouldShowTag === nextProps.shouldShowTag &&
        prevProps.canEditLesson === nextProps.canEditLesson &&
        prevProps.problemsInLessonCount === nextProps.problemsInLessonCount &&
        prevProps.hasNoTeams === nextProps.hasNoTeams
    );
});
